import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { marketsQueryOptions } from '@/features/markets';
import { storesQueryOptions } from '@/features/stores';

import { useBlockPreview } from '../../hooks/use-block-preview';
import { useActiveSlide } from '../editor/active-slide';

import type { PreviewDevice } from './banner-preview';
import { BlockPreviewPanelView } from './block-preview-panel-view';

/** Per browser: the store a shared block was last previewed in. */
export const PREVIEW_STORE_KEY = 'nucleus:content.previewStore';

function readPreviewStore(): string | null {
  try {
    return window.localStorage.getItem(PREVIEW_STORE_KEY);
  } catch {
    return null;
  }
}

function writePreviewStore(id: string): void {
  try {
    window.localStorage.setItem(PREVIEW_STORE_KEY, id);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
}

export interface BlockPreviewPanelProps {
  /** Scope of the block being edited: null for a shared block, previewed in a store picked here. */
  storeId: string | null;
}

/**
 * The live preview beside a block form (plan section 6). Must sit inside the editor's form. A
 * store block resolves in its own store; a shared one in the store picked in the toolbar,
 * remembered per browser. The market defaults to the store's default market.
 */
export function BlockPreviewPanel({ storeId }: BlockPreviewPanelProps) {
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [pickedStore, setPickedStore] = useState(readPreviewStore);
  const [marketChoice, setMarketChoice] = useState<{ storeId: string; marketId: string } | null>(
    null,
  );
  const { active, setActive } = useActiveSlide();
  // The preview keeps showing the last expanded slide when every card is collapsed.
  const [slide, setSlide] = useState(active ?? 0);
  if (active !== null && active !== slide) setSlide(active);

  const stores = useQuery(storesQueryOptions()).data ?? [];
  const previewStoreId =
    storeId ?? (stores.find((s) => s.id === pickedStore) ?? stores[0])?.id ?? null;
  const storeName = stores.find((s) => s.id === previewStoreId)?.name ?? '';

  const markets =
    useQuery({ ...marketsQueryOptions(previewStoreId ?? ''), enabled: previewStoreId !== null })
      .data ?? [];
  const market =
    (marketChoice?.storeId === previewStoreId
      ? markets.find((m) => m.id === marketChoice.marketId)
      : undefined) ??
    markets.find((m) => m.isDefault) ??
    markets[0];
  const country = market && !market.isDefault ? market.countries[0] : undefined;

  const preview = useBlockPreview({ storeId, previewStoreId, country });

  return (
    <BlockPreviewPanelView
      values={preview.values}
      preview={preview}
      device={device}
      onDeviceChange={setDevice}
      stores={storeId === null ? stores : null}
      storeId={previewStoreId}
      onStoreChange={(id) => {
        setPickedStore(id);
        writePreviewStore(id);
      }}
      storeName={storeName}
      markets={markets}
      marketId={market?.id ?? null}
      onMarketChange={(marketId) => {
        if (previewStoreId !== null) setMarketChoice({ storeId: previewStoreId, marketId });
      }}
      slide={slide}
      onSlideChange={(index) => {
        setSlide(index);
        setActive(index);
      }}
    />
  );
}
